import {
  ICD_REASON,
  MUSCOSKELETAL_RESILIENCE,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SCHEMA_VERSION,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import {
  createMaxLengthMessage,
  createMinLengthMessage,
  IKNumberSchema,
  INFECTIONS_CHARACTER_LIMIT,
  predicamentSchema,
  predicamentYesNoUnknownSchema,
  unixDateSchema,
} from "core/schemas/shared";
import { GKVMedicalFormData, SchemaVersion } from "core/types";
import { isInteger } from "lodash";
import { beeintraechtigungMap } from "onprem/mapping/mapFormToRehabRequest";
import Translations from "translations/types";
import { z } from "zod";
import { isFilled, stripEmptyFields, toNullishNumber } from "./utils";
// TODO: move createSimultaneousSchema to core/schemas
// eslint-disable-next-line no-outside-core-imports/no-outside-core-imports
import { createSimultaneousSchema } from "dsl/ecosystems/PatientAssessment/schemas/utils";

export const PER_ITEM_ERRORS = "perItemErrors" as const;

// OPS code format: starts with 1,3,5,6,8,9 then hyphen, two digits and one alphanumeric,
// followed by optional dot-separated segments of 1–2 alphanumerics, e.g. 5-000.01
const OPS_CODE_REGEX = /^[135689]-[0-9]{2}[a-zA-Z0-9](?:\.[a-zA-Z0-9]{1,2})*$/;

export function createBaseGKVMedicalFormSchema(translations: Translations) {
  // ICD rows 1–5
  const icdFields = Object.fromEntries(
    [1, 2, 3, 4, 5].flatMap((i) => {
      return [
        [
          `icd_code_${i}_diagnosis`,
          z
            .string()
            .max(150, createMaxLengthMessage(translations, 150))
            .nullish()
            .optional(),
        ],
        [
          `icd_code_${i}_code`,
          z.preprocess((val) => {
            if (val === "") return null;
            if (val && typeof val === "object" && "value" in val) {
              return val.value;
            }
            return val;
          }, z.string().nullish()),
        ],
        [
          `icd_code_${i}_side_localization_primary`,
          z.enum(["l", "r", "b"]).nullish().optional(),
        ],
        [
          `icd_code_${i}_reason`,
          z.preprocess((val) => {
            if (Array.isArray(val)) return val[0] ?? null;
            return val;
          }, z.enum(ICD_REASON).nullish().optional()),
        ],
      ];
    }),
  );

  // operation entries
  const operationFields = Object.fromEntries(
    Object.values(OPERATION_INDEXES).flatMap((index) => [
      [`operation_${index}_date`, z.number().int().nullish().optional()],
      [`operation_${index}_ops`, z.array(z.string()).nullish().optional()], // OPS format: ^[135689]-[0-9]{2}[a-zA-Z0-9](?:\.[a-zA-Z0-9]{1,2})*$
      [
        `operation_${index}_wound_closed`,
        z
          .union([
            z.literal(PREDICAMENT_STATE_YES),
            z.literal(PREDICAMENT_STATE_NO),
          ])
          .nullish()
          .optional(),
      ],
    ]),
  );

  // singer assessments
  // derive singer assessment fields from your map:
  //  - key  = 'food', 'dressing_and_undressing', …
  //  - value = '01', '02', … used for sorting
  const singerAssessmentFields = Object.entries(beeintraechtigungMap)
    .sort(([, codeA], [, codeB]) => Number(codeA) - Number(codeB))
    .reduce<Record<string, z.ZodTypeAny>>((shape, [fieldKey]) => {
      shape[`singer_assessment_${fieldKey}`] = z
        .number()
        .int()
        .min(0, createMinLengthMessage(translations, 0))
        .max(5, createMaxLengthMessage(translations, 5))
        .nullish()
        .optional();
      return shape;
    }, {});

  return z.object({
    admission_date: unixDateSchema.nullish().optional(),
    discharge_date: unixDateSchema.nullish().optional(),

    early_rehabilitation_measures: predicamentSchema.nullish().optional(),
    // I. Allgemeine Angaben
    gender: z.number().nullish().optional(),
    hospital_ik_number: IKNumberSchema.nullish().optional(),
    // I. IDs
    insurance_ik_number: IKNumberSchema.nullish().optional(),
    insurance_number: z
      .string()
      .min(10, createMinLengthMessage(translations, 10))
      .max(12, createMaxLengthMessage(translations, 12))
      .nullish()
      .optional(),
    patient_birthdate: unixDateSchema.nullish().optional(),
    patient_first_name: z
      .string()
      .max(45, createMaxLengthMessage(translations, 45))
      .nullish()
      .optional(),
    patient_last_name: z
      .string()
      .max(45, createMaxLengthMessage(translations, 45))
      .nullish()
      .optional(),
    rehab_admission_outside_two_weeks_explanation: z
      .string()
      .max(600, createMaxLengthMessage(translations, 600))
      .nullish()
      .optional(),
    rehab_admission_outside_two_weeks_start_date: z
      .number({
        error: () => translations.patientForms.fieldRequiredValidation,
      })
      .int()
      .nullish()
      .optional(),
    rehab_admission_type: z.enum(REHAB_ADMISSION).nullish().optional(),
    rehab_admission_within_two_weeks_start_date: z
      .number({
        error: () => translations.patientForms.fieldRequiredValidation,
      })
      .int()
      .nullish()
      .optional(),

    // II. Rehabilitationsbedürftigkeit
    ...icdFields,
    complication_cause_cardiovascular_description: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    complication_cause_maindiagnosis_description: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    complication_cause_other_description: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    complication_in_treatment_process: predicamentSchema.nullish().optional(),
    infections: z
      .string()
      .max(
        INFECTIONS_CHARACTER_LIMIT,
        createMaxLengthMessage(translations, INFECTIONS_CHARACTER_LIMIT),
      )
      .nullish()
      .optional(),
    infections_state: predicamentYesNoUnknownSchema.nullish().optional(),
    other_treatments: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    // singer assessments
    singer_assessment_date: z.number().int().nullish().optional(),

    surgery_performed: predicamentSchema.nullish().optional(),
    ...singerAssessmentFields,

    interim_prosthesis_fitted: predicamentSchema.nullish().optional(),
    // III. Rehabilitationsfähigkeit
    limited_capacity: predicamentSchema.nullish().optional(),
    patient_body_height: z.preprocess(
      toNullishNumber,
      z
        .number()
        .int()
        .min(10, createMinLengthMessage(translations, 2))
        .max(999, createMaxLengthMessage(translations, 3))
        .nullish()
        .optional(),
    ),
    patient_body_weight: z.preprocess(
      toNullishNumber,
      z
        .number()
        .int()
        .min(1, createMinLengthMessage(translations, 1))
        .max(999, createMaxLengthMessage(translations, 3))
        .nullish()
        .optional(),
    ),
    recommended_reha_type: z.enum(TYPE_OF_REHAB).nullish().optional(),

    recommended_reha_type_reason: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    reha_capacity: z.enum(MUSCOSKELETAL_RESILIENCE).nullish().optional(),
    reha_capacity_other: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    reha_capacity_partial: z.preprocess(
      toNullishNumber,
      z
        .number()
        .max(999, createMaxLengthMessage(translations, 999))
        .nullish()
        .optional(),
    ),
    rehab_directive_state: predicamentSchema.nullish().optional(),
    rehab_directive_type: z
      .array(z.enum(REHAB_DIRECTIVE_TYPE))
      .max(12, createMaxLengthMessage(translations, 12))
      .nullish()
      .optional(),
    rehab_focus: z.enum(REHAB_FOCUS).nullish().optional(),

    rehab_focus_other_description: z
      .string()
      .max(600, createMaxLengthMessage(translations, 600))
      .nullish()
      .optional(),
    // IV. Ziele/Prognose
    rehabilitation_goals: z
      .string()
      .max(600, createMaxLengthMessage(translations, 600))
      .nullish()
      .optional(),
    special_requirements: z
      .array(z.enum(SPECIAL_REQUIREMENTS))
      .nullish()
      .optional(),
    special_requirements_decubitus_description: z
      .enum(["1", "2", "3", "4"])
      .nullish()
      .optional(),
    special_requirements_dialysis_description: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    special_requirements_other_description: z
      .string()
      .max(600, createMaxLengthMessage(translations, 600))
      .nullish()
      .optional(),
    special_requirements_relatives_description: z
      .enum(["1", "2", "3", "4", "5"])
      .nullish()
      .optional(),
    // V. Special requirements
    special_requirements_required: predicamentSchema.nullish().optional(),
    special_requirements_weight_bed_description: z
      .string()
      .nullish()
      .optional(),

    // VI. Operations & Transport
    ...operationFields,
    approved_by: z
      .union([
        z.literal(PREDICAMENT_STATE_YES),
        z.literal(PREDICAMENT_STATE_NO),
      ])
      .nullish(),
    doctor_exam_date: unixDateSchema.nullish().optional(),
    doctor_in_charge_in_hospital_first_name: z
      .string()
      .max(45, createMaxLengthMessage(translations, 45))
      .nullish()
      .optional(),
    doctor_in_charge_in_hospital_last_name: z
      .string()
      .max(45, createMaxLengthMessage(translations, 45))
      .nullish()
      .optional(),
    doctor_in_charge_in_hospital_phone: z
      .string()
      .max(25, createMaxLengthMessage(translations, 25))
      .nullish()
      .optional(),
    require_accompanying_person: z
      .union([
        z.literal(PREDICAMENT_STATE_YES),
        z.literal(PREDICAMENT_STATE_NO),
      ])
      .nullish()
      .optional(),
    transport_type: z.enum(TRANSPORTATION_TYPE).nullish().optional(),
    transport_type_ambulance_description: z
      .string()
      .max(150, createMaxLengthMessage(translations, 150))
      .nullish()
      .optional(),
    transport_type_ambulance_specification: z
      .enum(TRANSPORTATION_SUBTYPE)
      .nullish()
      .optional(),
    transport_type_taxi_specification: z
      .enum(TRANSPORTATION_SUBTYPE)
      .nullish()
      .optional(),
  });
}

function createGKVMedicalFormValidator(
  translations: Translations,
  schemaVersion?: SchemaVersion,
): (
  data: GKVMedicalFormData,
) => { message: string; path: (string | number)[] }[] {
  return (data: GKVMedicalFormData) => {
    const issues: { message: string; path: (string | number)[] }[] = [];

    // Required field validations (previously handled by base schema)
    if (!data.gender) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["gender"],
      });
    }

    if (!isFilled(data.patient_first_name)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne.firstNameRequired,
        path: ["patient_first_name"],
      });
    }

    if (!isFilled(data.patient_last_name)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne.lastNameRequired,
        path: ["patient_last_name"],
      });
    }

    if (!isFilled(data.insurance_number)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne
            .insuranceNumberRequired,
        path: ["insurance_number"],
      });
    }

    if (!isFilled(data.patient_birthdate)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne.dateOfBirthRequired,
        path: ["patient_birthdate"],
      });
    }

    if (!isFilled(data.admission_date)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["admission_date"],
      });
    }

    if (!isFilled(data.discharge_date)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["discharge_date"],
      });
    }

    if (!isFilled(data.early_rehabilitation_measures)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["early_rehabilitation_measures"],
      });
    }

    if (!data.rehab_admission_type) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["rehab_admission_type"],
      });
    }

    if (!isFilled(data.surgery_performed)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["surgery_performed"],
      });
    }

    if (!isFilled(data.complication_in_treatment_process)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["complication_in_treatment_process"],
      });
    }

    if (!isFilled(data.singer_assessment_date)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["singer_assessment_date"],
      });
    }

    if (!isFilled(data.limited_capacity)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["limited_capacity"],
      });
    }

    if (!isFilled(data.interim_prosthesis_fitted)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["interim_prosthesis_fitted"],
      });
    }

    if (!isFilled(data.rehabilitation_goals)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["rehabilitation_goals"],
      });
    }

    if (!data.rehab_focus) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["rehab_focus"],
      });
    }

    if (!data.recommended_reha_type) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["recommended_reha_type"],
      });
    }

    if (!isFilled(data.rehab_directive_state)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["rehab_directive_state"],
      });
    }

    if (!isFilled(data.special_requirements_required)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["special_requirements_required"],
      });
    }

    if (!data.transport_type) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["transport_type"],
      });
    }

    if (!isFilled(data.require_accompanying_person)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["require_accompanying_person"],
      });
    }

    if (!isFilled(data.doctor_in_charge_in_hospital_first_name)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne.firstNameRequired,
        path: ["doctor_in_charge_in_hospital_first_name"],
      });
    }

    if (!isFilled(data.doctor_in_charge_in_hospital_last_name)) {
      issues.push({
        message:
          translations.patientForms.medicalForm.sectionOne.lastNameRequired,
        path: ["doctor_in_charge_in_hospital_last_name"],
      });
    }

    if (!isFilled(data.doctor_in_charge_in_hospital_phone)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["doctor_in_charge_in_hospital_phone"],
      });
    }

    if (!isFilled(data.doctor_exam_date)) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["doctor_exam_date"],
      });
    }

    // Complications
    if (data.complication_in_treatment_process === PREDICAMENT_STATE_YES) {
      const main = isFilled(data.complication_cause_maindiagnosis_description);
      const cardio = isFilled(
        data.complication_cause_cardiovascular_description,
      );
      const other = isFilled(data.complication_cause_other_description);

      if (!main && !cardio && !other) {
        issues.push({
          message:
            translations.patientForms.medicalForm.sectionTwo
              .validationComplication,
          path: ["complication_cause_maindiagnosis_description"],
        });
        issues.push({
          message:
            translations.patientForms.medicalForm.sectionTwo
              .validationComplication,
          path: ["complication_cause_cardiovascular_description"],
        });
        issues.push({
          message:
            translations.patientForms.medicalForm.sectionTwo
              .validationComplication,
          path: ["complication_cause_other_description"],
        });
      }
    }

    if (!isFilled(data.infections_state)) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["infections_state"],
      });
    }

    if (
      data.infections_state === PREDICAMENT_STATE_YES &&
      !isFilled(data.infections)
    ) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["infections"],
      });
    }

    // singer assessment fields
    const singerAssessmentFields = Object.entries(beeintraechtigungMap)
      .sort(([, codeA], [, codeB]) => Number(codeA) - Number(codeB))
      .reduce<string[]>((shape, [fieldKey]) => {
        shape.push(`singer_assessment_${fieldKey}`);
        return shape;
      }, []);
    singerAssessmentFields.forEach((field) => {
      // @ts-ignore
      if (!isFilled(data[field])) {
        issues.push({
          message: translations.patientForms.generalForm.fieldRequired,
          path: [field],
        });
      }
    });

    if (
      data.rehab_admission_type === REHAB_ADMISSION.WITHIN_TWO_WEEKS &&
      !isFilled(data.rehab_admission_within_two_weeks_start_date)
    ) {
      // Verlegung validations
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["rehab_admission_within_two_weeks_start_date"],
      });
    }

    if (
      data.rehab_admission_type === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
      !isFilled(data.rehab_admission_outside_two_weeks_start_date)
    ) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["rehab_admission_outside_two_weeks_start_date"],
      });
    }

    if (
      data.rehab_admission_type === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
      !isFilled(data.rehab_admission_outside_two_weeks_explanation)
    ) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["rehab_admission_outside_two_weeks_explanation"],
      });
    }

    // Reha capcity
    if (data.reha_capacity === MUSCOSKELETAL_RESILIENCE.PARTIALLY) {
      if (!isFilled(data.reha_capacity_partial)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["reha_capacity_partial"],
        });
      } else if (
        data.reha_capacity_partial &&
        !isInteger(data.reha_capacity_partial)
      ) {
        issues.push({
          message: translations.patientForms.integerLimitValidation,
          path: ["reha_capacity_partial"],
        });
      }
    }

    //  Fall nach § 16 Reha-RL gegeben
    if (
      data.rehab_directive_state === PREDICAMENT_STATE_YES &&
      (!data.rehab_directive_type || data.rehab_directive_type.length === 0)
    ) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["rehab_directive_type"],
      });
    }

    // // Only validate the first row as required
    const firstDiagnosis = data.icd_code_1_diagnosis;
    const firstCode = data.icd_code_1_code;
    const firstReason = data.icd_code_1_reason;

    const firstDiagnosisFilled = isFilled(firstDiagnosis);
    const firstCodeFilled = isFilled(firstCode);
    const firstReasonFilled = isFilled(firstReason);

    // Only show errors for the first row if it's incomplete
    if (!firstDiagnosisFilled) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["icd_code_1_diagnosis"],
      });
    }
    if (!firstCodeFilled) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["icd_code_1_code"],
      });
    }
    if (!firstReasonFilled) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["icd_code_1_reason"],
      });
    }

    // For ICD codes 2 through 5, the fields are optional as a group.
    // However, if any one of the fields in a group has a value, all fields in that group become required.
    for (let i = 2; i <= 5; i++) {
      const diagnosisField =
        `icd_code_${i}_diagnosis` as keyof GKVMedicalFormData;
      const codeField = `icd_code_${i}_code` as keyof GKVMedicalFormData;
      const reasonField = `icd_code_${i}_reason` as keyof GKVMedicalFormData;

      const diagnosisFilled = isFilled(data[diagnosisField]);
      const codeFilled = isFilled(data[codeField]);
      const reasonFilled = isFilled(data[reasonField]);

      const anyFilled = diagnosisFilled || codeFilled || reasonFilled;

      // If any field in the group is filled, validate that all fields in the group are filled.
      // If all fields in the group are empty, this condition is false, and no errors are pushed.
      if (anyFilled) {
        if (!diagnosisFilled) {
          issues.push({
            message: translations.patientForms.fieldRequiredValidation,
            path: [diagnosisField],
          });
        }
        if (!codeFilled) {
          issues.push({
            message: translations.patientForms.fieldRequiredValidation,
            path: [codeField],
          });
        }
        if (!reasonFilled) {
          issues.push({
            message: translations.patientForms.fieldRequiredValidation,
            path: [reasonField],
          });
        }
      }
    }

    // Transport
    if (
      data.transport_type === TRANSPORTATION_TYPE.AMBULANCE &&
      !data.transport_type_ambulance_specification
    ) {
      issues.push({
        message: translations.patientForms.generalForm.fieldRequired,
        path: ["transport_type_ambulance_specification"],
      });
    }

    if (
      data.rehab_focus === REHAB_FOCUS.OTHER &&
      !isFilled(data.rehab_focus_other_description)
    ) {
      issues.push({
        message: translations.patientForms.fieldRequiredValidation,
        path: ["rehab_focus_other_description"],
      });
    }

    // Special Reha Requirements
    if (data.special_requirements_required === PREDICAMENT_STATE_YES) {
      const selected =
        Array.isArray(data.special_requirements) &&
        data.special_requirements.length > 0;

      if (!selected) {
        issues.push({
          message:
            "Mindestens eine besondere Anforderung muss ausgewählt werden.",
          path: ["special_requirements"],
        });
      }

      const selectedReqs = data.special_requirements ?? [];
      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.DIALYSIS) &&
        !isFilled(data.special_requirements_dialysis_description)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["special_requirements_dialysis_description"],
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.OTHER) &&
        !isFilled(data.special_requirements_other_description)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["special_requirements_other_description"],
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.RELATIVES) &&
        !isFilled(data.special_requirements_relatives_description)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["special_requirements_relatives_description"],
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.DECUBITUS) &&
        !isFilled(data.special_requirements_decubitus_description)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["special_requirements_decubitus_description"],
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.WEIGHT_BED) &&
        !isFilled(data.special_requirements_weight_bed_description)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["special_requirements_weight_bed_description"],
        });
      }
    }

    if (schemaVersion === SCHEMA_VERSION.V2_0_0) {
      if (!isFilled(data.patient_body_height)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["patient_body_height"],
        });
      }
      if (!isFilled(data.patient_body_weight)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["patient_body_weight"],
        });
      }
    }

    // Surgeries
    let completeOperationCount = 0;

    for (const index of Object.values(OPERATION_INDEXES)) {
      const date = data[`operation_${index}_date`];
      const ops = data[`operation_${index}_ops`];
      const wound = data[`operation_${index}_wound_closed`];

      const hasDate = isFilled(date);
      const hasWound = isFilled(wound);
      const hasOPS = Array.isArray(ops) && ops.some(isFilled);

      const filledFields = [hasDate, hasWound, hasOPS].filter(Boolean).length;

      if (filledFields === 3) {
        completeOperationCount++;
      } else if (filledFields > 0) {
        // Only show field-level errors on missing ones
        if (!hasDate) {
          issues.push({
            message: translations.patientForms.fieldRequiredValidation,
            path: [`operation_${index}_date`],
          });
        }
        if (!hasWound) {
          issues.push({
            message: translations.patientForms.generalForm.fieldRequired,
            path: [`operation_${index}_wound_closed`],
          });
        }
        if (!hasOPS) {
          issues.push({
            message: translations.patientForms.fieldRequiredValidation,
            path: [`operation_${index}_ops`],
          });
        }
      }

      // Validate each OPS code: must be a string and match OPS format
      if (Array.isArray(ops)) {
        const messages: (string | null)[] = Array(ops.length).fill(null);

        ops.forEach((code, i) => {
          if (!isFilled(code)) return;

          if (typeof code !== "string") {
            messages[i] =
              translations.patientForms.medicalForm.sectionTwo.validationString;
          } else if (!OPS_CODE_REGEX.test(code)) {
            messages[i] =
              translations.patientForms.medicalForm.sectionTwo.validationOpsInvalid;
          }
        });

        if (messages.some(Boolean)) {
          issues.push({
            message: JSON.stringify({ messages, type: PER_ITEM_ERRORS }),
            path: [`operation_${index}_ops`],
          });
        }
      }

      // Validate recommended_reha_type_reason
      if (
        (data.recommended_reha_type === TYPE_OF_REHAB.AMBULANT_MOBIL ||
          data.recommended_reha_type === TYPE_OF_REHAB.STATIONARY) &&
        !isFilled(data.recommended_reha_type_reason)
      ) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: ["recommended_reha_type_reason"],
        });
      }
    }

    // If surgery is marked as performed but no full operation is present
    if (
      data.surgery_performed === PREDICAMENT_STATE_YES &&
      completeOperationCount === 0
    ) {
      const firstIndex = OPERATION_INDEXES.OP1;

      const firstDate = data[`operation_${firstIndex}_date`];
      const firstOps = data[`operation_${firstIndex}_ops`];
      const firstWound = data[`operation_${firstIndex}_wound_closed`];

      if (!isFilled(firstDate)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: [`operation_${firstIndex}_date`],
        });
      }

      if (!isFilled(firstWound)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: [`operation_${firstIndex}_wound_closed`],
        });
      }

      if (!Array.isArray(firstOps) || !firstOps.some(isFilled)) {
        issues.push({
          message: translations.patientForms.fieldRequiredValidation,
          path: [`operation_${firstIndex}_ops`],
        });
      }
    }

    if (!isFilled(data.approved_by)) {
      issues.push({
        message: translations.assessments.formValidation.singleSelectError,
        path: ["approved_by"],
      });
    }

    return issues;
  };
}

export function getGKVMedicalFormSchema({
  schemaVersion,
  skipRequiredValidation,
  translations,
}: {
  schemaVersion?: SchemaVersion;
  skipRequiredValidation?: boolean;
  translations: Translations;
}) {
  const baseSchema = createBaseGKVMedicalFormSchema(translations);
  const partialSchema = baseSchema.partial();

  if (skipRequiredValidation) {
    return z.preprocess(stripEmptyFields, partialSchema);
  }

  const validator = createGKVMedicalFormValidator(translations, schemaVersion);
  return createSimultaneousSchema(baseSchema, validator);
}
